import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Toast } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { Typeahead } from 'react-bootstrap-typeahead';

import { allDepartmentDropdown, createAuditMutation, GET_REQUEST_FY_BY_BARCODE, updateSalvageMutation } from './api';
import * as selectDropdowns from '../RequestForms/utils';
import { allDispCodes, eoneDispositionCodes } from './utils';
import { Comments } from '../Comments';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { TooltipButton } from '../TooltipButton';

enum salvageCodes {
  'disposal',
  'pca',
  'psm',
  'ma',
}

export const SalvageApprovalForm = (props: any) => {
  const [comment, setComment] = React.useState('');
  const [createAudit] = useMutation(createAuditMutation);
  const [updateSalvage] = useMutation(updateSalvageMutation);
  const [toastVisible, setToastVisible] = React.useState(false);
  const [elevated, setElevated] = React.useState(false);
  const { user } = useSelector((state: any) => ({
    user: state.user,
  }));

  React.useEffect(() => {
    let enableDisabledFields = false;
    if (user.roles) {
      user.roles.forEach((role) => {
        switch (role.roleId) {
          case 1: // admin
          case 3: // pc - non cap
          case 10: // pc - cap
          case 21: // rec super
            enableDisabledFields = true;
        }
      });
    }
    setElevated(enableDisabledFields);
  }, []);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      assetNumber: props.queryData.assetNumber ? props.queryData.assetNumber : '',
      barCode: props.queryData.barCode,
      description1: props.queryData.description1,
      description2: props.queryData.description2,
      description3: props.queryData.description3,
      additionalRemarks: props.queryData.additionalRemarks,
      additionalRemarks2: props.queryData.additionalRemarks2,
      additionalRemarks3: props.queryData.additionalRemarks3,
      physicalOwner: props.queryData.physicalOwner ? props.queryData.physicalOwner : null,
      businessUnit: props.queryData.businessUnit,
      department: props.queryData.department,
      location: props.queryData.location,
      serialNumber: props.queryData.serialNumber,
      updateStatus: props.queryData.updateStatus,
      salvageId: props.queryData.salvageId,
      salvageFormDispositionCode: props.queryData.salvageFormDispositionCode,
      enterpriseOneDispositionCode: props.queryData.enterpriseOneDispositionCode,
      disposalApproval: props.queryData.disposalApproval ? props.queryData.disposalApproval : user.email,
      disposalApprovalDate: props.queryData.disposalApprovalDate,
      pcaApproval: !props.queryData.pcaApproval && props.queryData.disposalApproval ? user.email : props.queryData.pcaApproval,
      pcaApprovalDate: props.queryData.pcaApprovalDate,
      // props.queryData.pcaApprovalDate && props.queryData.disposalApprovalDate ? props.queryData.pcaApprovalDate : new Date().toDateString(),
      psmApproval:
        !props.queryData.psmApproval && props.queryData.pcaApproval
          ? user.email
          : !props.queryData.pcaApproval
          ? props.queryData.psmApproval
          : user.email,
      psmApprovalDate: props.queryData.psmApprovalDate,
      maApproval:
        !props.queryData.maApproval && props.queryData.psmApproval
          ? user.email
          : !props.queryData.psmApproval
          ? props.queryData.maApproval
          : user.email,
      maApprovalDate: props.queryData.maApprovalDate,
      // disposalCheck: false,
      // pcaCheck: false,
      // psmCheck: false,
      // maCheck: false
      condition: props.queryData.condition,
      heatTicket: props.queryData.heatTicket,
    },
  });

  React.useEffect(() => {
    let result = allDispCodes.filter((item, index) => {
      return item.code === props.queryData.salvageFormDispositionCode;
    })[0];
    try{
      setValue('salvageFormDispositionCode', `${result.code} - ${result.name}`)
    }catch(e){
      setValue('salvageFormDispositionCode', '? - Invalid Code')
    }
  }, []);

  const getApprovalStage = () => {
    if (!props.queryData.disposalApproval) return salvageCodes.disposal;
    if (props.queryData.disposalApproval && !props.queryData.pcaApproval) return salvageCodes.pca;
    // last two verifications not needed
    // if (props.queryData.disposalApproval && props.queryData.pcaApproval && !props.queryData.psmApproval) return salvageCodes.psm;
    // if (props.queryData.disposalApproval && props.queryData.pcaApproval && props.queryData.psmApproval && !props.queryData.maApproval)
    //   return salvageCodes.ma;
    return -1;
  };

  const { data } = useQuery(GET_REQUEST_FY_BY_BARCODE, {
    variables: { barcode: props.queryData.barCode },
  });

  const { data: deptData } = useQuery(allDepartmentDropdown);

  const createAuditForSalvage = (formValues, update: boolean = false) => {
    let commentStr =
      comment !== ''
        ? `Comment: ${comment} :: ${user.adId} has ${getApprovalStage() === 1 ? 'completed' : update ? 'updated' : 'approved'} salvage of barcode ${formValues.barCode}`
        : `${user.adId} has ${getApprovalStage() === 1 ? 'completed' : update ? 'updated' : 'approved'} salvage of barcode ${formValues.barCode}`;
    const audit = {
      barcode: formValues.barCode,
      createdBy: user.adId,
      lastUpdatedBy: user.adId,
      comment: commentStr,
      updatedOn: new Date(),
      entryType: 'Salvage',
    };
    createAudit({ variables: audit })
      .catch((err) => console.log('Error: /frontend/src/components/AssetRelocationForms//createAuditForSalvage', err));
  };

  const deptDropdown = () => {
    return !deptData ? [] : deptData.readDepartments;
  };

  const handleUpdate = async () => {
    setValue('salvageFormDispositionCode', getValues().salvageFormDispositionCode.substring(0, 1));
    // getValues().salvageFormDispositionCode = getValues().salvageFormDispositionCode.substring(0, 1);
    
    let approvalStage = getApprovalStage();
    createAuditForSalvage(getValues(), true);

    updateSalvage({
      variables: {
        input: getValues(),
        approval: approvalStage,
        update: true,
      },
    })
      .then(async (res) => {
        if (res.data.updateSalvage.salvageId === -1) {
          throw Error('Asset salvage status update failed!');
        }
      })
      .then(() => {
        // createAuditForSalvage(data);
        setToastVisible(!toastVisible);
      })
      .catch((err) => {
        alert(err);
      })
      .finally(() => {
        // alert('Transfer Completed Successfully, redirecting...')
        setTimeout(() => {
          window.location.replace('/salvage');
        }, 1500);
      });
  };

  const onSubmit = handleSubmit(async (data) => {
    //determine email from checks
    //delete check data before mutation
    // remove human readable sfdc name below
    data.salvageFormDispositionCode = data.salvageFormDispositionCode.substring(0, 1);
    let approvalStage = getApprovalStage();
    createAuditForSalvage(data);
    // console.log('approvalStage: ', approvalStage)
    if (approvalStage < 0) throw Error('Invalid Approval Order...');
    updateSalvage({
      variables: {
        input: data,
        approval: approvalStage,
      },
    })
      .then(async (res) => {
        if (res.data.updateSalvage.salvageId === -1) {
          throw Error('Asset salvage status update failed!');
        }
      })
      .then(() => {
        // createAuditForSalvage(data);
        setToastVisible(!toastVisible);
      })
      .catch((err) => {
        alert(err);
      })
      .finally(() => {
        // alert('Transfer Completed Successfully, redirecting...')
        setTimeout(() => {
          window.location.replace('/salvage');
        }, 1500);
      });
  });

  return (
    <React.Fragment>
      <form id='transferForm' onSubmit={onSubmit}>
        {props.queryData.assetNumber ? (
          <div>
            <h2>Process Salvage for Asset {props.queryData.assetNumber}</h2>
          </div>
        ) : (
          <div>
            <h2>Process Salvage</h2>
          </div>
        )}
        <div className='row justify-content-start'>
          <div className='col-sm-9 mt-2'>
            {props.queryData.updateStatus !== 'Salvage Approved' && (
              <Button type='submit' variant='info' form='transferForm' className='float-left mr-2'>
                {getApprovalStage() === salvageCodes.pca ? 'Approve and Complete' : 'Approve and Send'}
              </Button>
            )}
            {elevated && (
              <TooltipButton
                buttonClass='mr-2 float-left'
                buttonLabel='Update'
                onClick={handleUpdate}
                type='submit'
                tooltipLabel='Update salvage information without approving'
                variant='outline-info'
              />
            )}
            <Button onClick={() => window.location.replace('/salvage')} variant='primary' className='float-left'>
              Close
            </Button>
            {data && data.readRequestFYByBarcode.requestNumber && (
              <Button
                className='ml-2'
                variant='danger'
                id='reqNumber'
                onClick={() => {
                  window.open(`/receiving/view/requestId=${data.readRequestFYByBarcode.requestId}`);
                }}>{`View Request: ${data.readRequestFYByBarcode.requestNumber}`}</Button>
            )}
          </div>
        </div>
        <Toast
          autohide
          style={{ backgroundColor: '#454545', color: '#ffffff', position: 'absolute', top: 75, right: '50%' }}
          show={toastVisible}
          onClose={() => setToastVisible(!toastVisible)}>
          <Toast.Body>Salvage Updated Successfully!</Toast.Body>
        </Toast>
        <div className='row'>
          <div className='col-sm-8'>
            <div className='row justify-content-start mt-3'>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Asset Number</label>
                  <input disabled className='form-control col-sm' {...register('assetNumber')} />
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Barcode</label>
                  <input className='form-control col-sm' disabled {...register('barCode')} />
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Serial Number</label>
                  <input className='form-control col-sm' disabled {...register('serialNumber')} />
                </div>
              </div>
            </div>
            <div className='row justify-content-start mt-3'>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Description 1</label>
                  <input maxLength={30} className='form-control col-sm' {...register('description1')} />
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Description 2</label>
                  <input maxLength={30} className='form-control col-sm' {...register('description2')} />
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Description 3</label>
                  <input className='form-control col-sm' disabled {...register('description3')} />
                </div>
              </div>
            </div>
            <div className='row justify-content-start mt-3'>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Additional Remarks</label>
                  <input maxLength={30} className='form-control col-sm' {...register('additionalRemarks')} />
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Additional Remarks 2</label>
                  <input maxLength={30} className='form-control col-sm' {...register('additionalRemarks2')} />
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Additional Remarks 3</label>
                  <input maxLength={25} className='form-control col-sm' {...register('additionalRemarks3')} />
                </div>
              </div>
            </div>

            <div className='row justify-content-start mt-3'>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Department</label>
                  <Controller
                    control={control}
                    name='department'
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <Typeahead
                        id='department'
                        labelKey={(option) => `${option.Abbreviation} - ${option.Name}`}
                        options={deptDropdown()}
                        onChange={(e) => onChange(e[0] ? e[0].Abbreviation : null)}
                      />
                    )}
                  />
                  <label>{`(Current: ${getValues().department})`}</label>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Physical Owner</label>
                  <Controller
                    control={control}
                    name='physicalOwner'
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <Typeahead
                        id='physicalOwner'
                        labelKey={(option) => `${option.Code} - ${option.Description}`}
                        options={selectDropdowns.physicalOwners}
                        onChange={(e) => onChange(e[0] ? e[0].Code.toString() : null)}
                      />
                    )}
                  />
                  <label>{`(Current: ${getValues().physicalOwner})`}</label>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Location</label>
                  <input disabled className='form-control col-sm' {...register('location', { required: true })} />
                </div>
              </div>
            </div>
            <div className='row justify-content-start mt-3'>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Salvage Form Disposition Code</label>
                  <input className='form-control col-sm' {...register('salvageFormDispositionCode')} />
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Enterprise One Disposition Code</label>
                  <select className='form-control' {...register('enterpriseOneDispositionCode')}>
                    {eoneDispositionCodes.map((value, index) => {
                      return <option key={value.code + value.name} value={value.code}>{`${value.code}: ${value.name}`}</option>;
                    })}
                  </select>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Business Unit</label>
                  <input disabled className='form-control col-sm' {...register('businessUnit')} />
                </div>
              </div>
            </div>

            <div className='row justify-content-start mt-3'>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Disposal Approval</label>
                  <input disabled={props.queryData.disposalApproval} className='form-control col-sm' {...register('disposalApproval')} />
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Disposal Approval Date</label>
                  <Controller
                    control={control}
                    name='disposalApprovalDate'
                    rules={{ required: !props.queryData.disposalApprovalDate }}
                    render={({ field: { onChange, value } }) => (
                      <DatePicker
                        disabled={props.queryData.disposalApprovalDate}
                        openToDate={new Date()}
                        className='form-control col-sm'
                        selected={value ? new Date(value) : value}
                        onChange={onChange}
                      />
                    )}
                  />
                  <span style={{ color: 'red' }}>{errors.disposalApprovalDate && 'Please select a date!'}</span>
                  {/* <ErrorMessage errors={!errors ? new Object() : errors} name='disposalApprovalDate'/>       */}
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Condition</label>
                  <input disabled className='form-control col-sm' {...register('condition')} />
                </div>
              </div>
            </div>
            <div className='row justify-content-start mt-3'>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>PC&A Approval</label>
                  <input disabled={props.queryData.pcaApproval} className='form-control col-sm' {...register('pcaApproval')} />
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>PC&A Approval Date</label>
                  <Controller
                    control={control}
                    name='pcaApprovalDate'
                    rules={{ required: props.queryData.disposalApproval && !props.queryData.pcaApproval }}
                    render={({ field: { onChange, value } }) => (
                      <DatePicker
                        disabled={props.queryData.pcaApprovalDate}
                        openToDate={new Date()}
                        className='form-control col-sm'
                        selected={value ? new Date(value) : value}
                        onChange={onChange}
                      />
                    )}
                  />
                  <span style={{ color: 'red' }}>{errors.pcaApprovalDate && 'Please select a date!'}</span>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Heat Ticket #</label>
                  <input className='form-control col-sm' {...register('heatTicket')} />
                </div>
              </div>
            </div>
            {/* <div className='row justify-content-start mt-3'>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>PSM Approval</label>
                  <input disabled={props.queryData.psmApproval} className='form-control col-sm' {...register('psmApproval')} />
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>PSM Approval Date</label>
                  <Controller
                    control={control}
                    name='psmApprovalDate'
                    rules={{ required: props.queryData.pcaApproval && !props.queryData.psmApproval }}
                    render={({ field: { onChange, value } }) => (
                      <DatePicker
                        disabled={props.queryData.psmApprovalDate}
                        openToDate={new Date()}
                        className='form-control col-sm'
                        selected={value ? new Date(value) : value}
                        onChange={onChange}
                      />
                    )}
                  />
                  <span style={{ color: 'red' }}>{errors.psmApprovalDate && 'Please select a date!'}</span>
                </div>
              </div>
            </div>
            <div className='row justify-content-start mt-3'>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>MA Approval</label>
                  <input disabled={props.queryData.maApproval} className='form-control col-sm' {...register('maApproval')} />
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>MA Approval Date</label>
                  <Controller
                    control={control}
                    name='maApprovalDate'
                    rules={{
                      required: props.queryData.psmApproval && !props.queryData.maApproval,
                    }}
                    render={({ field: { onChange, value } }) => (
                      <DatePicker
                        disabled={props.queryData.maApprovalDate}
                        openToDate={new Date()}
                        className='form-control col-sm'
                        selected={value ? new Date(value) : value}
                        onChange={onChange}
                      />
                    )}
                  />
                  <span style={{ color: 'red' }}>{errors.maApprovalDate && 'Please select a date!'}</span>
                </div>
              </div>
            </div> */}
            <div>
              <label>
                <span style={{ color: '#f00' }}>*&nbsp;</span>
                Changes made on this form are for record-keeping in the Asset Inventory system.
                <br />
                &nbsp;&nbsp;&nbsp;Asset changes still need to be made in EnterpriseOne.
              </label>
            </div>
          </div>

          <div className='col-sm-4 mt-3'>
            <div className='form-group'>
              <label>Add Comment</label>
              <textarea className='form-control col-sm' id='comment' rows={1} value={comment} onChange={(e) => setComment(e.target.value)} />
            </div>
            <Comments barcode={props.queryData.barCode} style={{ height: '600px' }} />
          </div>
        </div>
      </form>
    </React.Fragment>
  );
};
